import { api } from '../api';
import { initAdminShell, escapeHtml, showError } from './shell';
import { createPagination, type PagedResult } from './pagination';
import { attachSearch } from './search';
import { t } from '../i18n/i18n';

interface AuditLogRow {
  id: number;
  timestamp: string;
  actorEmail: string;
  action: string;
  resourceType: string;
  resourceId: string | null;
  details: string | null;
}

const PAGE_SIZE = 25;

(async () => {
  try {
    await initAdminShell('audit-logs');
  } catch {
    return;
  }

  const tbody = document.getElementById('al-tbody');
  if (!tbody) return;

  const pagination = createPagination(1);
  let query = '';

  async function load(page = pagination.page): Promise<void> {
    try {
      const client = await api();
      const { data } = await client.get<PagedResult<AuditLogRow>>('/api/admin/audit-logs', {
        params: { page, pageSize: PAGE_SIZE, ...(query ? { search: query } : {}) },
      });
      render(data.items);
      pagination.render('al-prev', 'al-next', 'al-pageinfo', 'al-pagination', data.total, data.pageSize);
    } catch (err) {
      showError('al-error', err instanceof Error ? err.message : 'Failed to load audit logs.');
    }
  }

  function render(rows: AuditLogRow[]): void {
    if (!tbody) return;
    if (rows.length === 0) {
      tbody.innerHTML = `<tr><td colspan="5" class="req-empty">${escapeHtml(t('admin.audit_logs.empty'))}</td></tr>`;
      return;
    }
    tbody.innerHTML = rows.map((r) => `
      <tr>
        <td><time datetime="${escapeHtml(r.timestamp)}">${escapeHtml(formatTimestamp(r.timestamp))}</time></td>
        <td>${escapeHtml(r.actorEmail)}</td>
        <td><code>${escapeHtml(r.action)}</code></td>
        <td>${escapeHtml(r.resourceType)}${r.resourceId ? ` <code>${escapeHtml(r.resourceId)}</code>` : ''}</td>
        <td>${r.details ? `<details><summary>view</summary><pre>${escapeHtml(formatDetails(r.details))}</pre></details>` : ''}</td>
      </tr>
    `).join('');
  }

  pagination.attach('al-prev', 'al-next', (page) => void load(page));

  const searchInput = document.getElementById('al-search') as HTMLInputElement | null;
  if (searchInput) {
    attachSearch(searchInput, (q) => {
      query = q;
      pagination.reset();
      void load(1);
    });
  }

  await load();
})();

function formatTimestamp(iso: string): string {
  // Render as "YYYY-MM-DD HH:MM:SS" in the user's locale rather than the
  // raw ISO; the original is preserved in the <time datetime> attribute.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

function formatDetails(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
